import { Button, Card, Empty, Flex, Form, Input, Select, Space, Spin } from "antd"
import { useWatch } from "antd/es/form/Form"
import { PlusOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { isFeatureEnabled } from "config/features"
import { paypalLogoUrl } from "helpers/constant"
import { numberToCurrency } from "helpers/string"
import { colors, spacings } from "themes/variables"
import { Text } from "ui/Typography"
import NoCardImage from "ui/Icon/DSIcon/NoCardImage"
import TipSection from "ui/TipSection"
import PaymentMethodList from "pagesV2/pro/Bookings/BookingForm/PaymentMethodList"
import AddCardModal from "pagesV2/pro/Bookings/BookingForm/AddCardModal"
import stripeIcon from "assets/imagesV2/settings/stripe.svg"
import { getFormattedBookingDate } from "ui/Booking/Details/utils"
import { Avatar } from "ui/Avatar"
import { getMediaUrl } from "utils/media-upload"
import { LINK_PROVIDERS, OTHERS_TYPES, PAYMENT_TABS } from "./constants"
import { useTakePaymentForm } from "./useTakePaymentForm"
import styles from "./index.module.css"

const TakePaymentForm = ({ remainingBalance, bookingUUID, bookingId, onClose, bookingTitle, bookedOn, clientName, clientEmail, experienceAvatar }) => {
  const [form] = Form.useForm()
  const [modalForm] = Form.useForm()
  const { t } = useTranslation()

  const {
    activeTab, setActiveTab,
    linkProvider, setLinkProvider,
    tipAmount, setTipAmount,
    selectedCardId, setSelectedCardId,
    isModalVisible, setIsModalVisible,
    isAddingCard, isSubmitting,
    isStripeConnected, isPaypalConnected,
    paymentMethods, paymentMethodsLoading,
    clientId,
    visibleTabs, linkInfoText, amountLabel, submitLabel,
    handleSubmit, createPaymentMethod
  } = useTakePaymentForm({ remainingBalance, bookingUUID, bookingId, onClose, clientEmail, form, modalForm })

  const amountValue = useWatch("amount", form)
  const baseAmount = Number(amountValue) || 0
  const totalToPay = baseAmount + tipAmount

  return (
    <Spin spinning={isSubmitting} tip="Processing payment...">
      <Space direction="vertical" size={24} className="w-full">
        <div className={styles.formContent}>
          <Space direction="vertical" size={24} className="w-full">
            {(bookingTitle || clientName || clientEmail) && (
              <Flex gap={12} align="center" className={styles.bookingInfo}>
                {getMediaUrl(experienceAvatar) && (
                  <Avatar fullName={bookingTitle} src={getMediaUrl(experienceAvatar)} shape="square" size={64} />
                )}
                <Flex vertical gap={4}>
                  {bookingTitle && (
                    <Text fw={500}>{bookingTitle}{bookedOn ? ` · ${getFormattedBookingDate(bookedOn)}` : ""}</Text>
                  )}
                  {(clientName || clientEmail) && (
                    <Text color={colors.textSecondaryDark}>
                      {[clientName, clientEmail].filter(Boolean).join(" · ")}
                    </Text>
                  )}
                </Flex>
              </Flex>
            )}

            <Flex gap={spacings.S} style={{ backgroundColor: colors.surface1 }} className="py-xs px-s">
              <Text color={colors.textSecondaryDark}>{t("client.payment.outstandingBalance")}</Text>
              <Text color={colors.textPrimary} fw={500}>{numberToCurrency(remainingBalance)}</Text>
            </Flex>

            <div>
              <Text className={styles.sectionLabel}>CHOOSE PAYMENT METHOD</Text>
              <div className={styles.tabContainer}>
                {visibleTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={activeTab === tab.key ? styles.tabButtonActive : styles.tabButton}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <Form form={form} layout="vertical" initialValues={{ amount: remainingBalance }}>
              <Form.Item
                label={amountLabel}
                name="amount"
                rules={[
                  { required: true, message: "Amount is required" },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.reject()
                      const num = Number.parseFloat(value)
                      if (Number.isNaN(num) || num <= 0) return Promise.reject("Enter a valid amount")
                      if (num > remainingBalance) return Promise.reject(t("client.payment.amountToPayCannotExceedOutstandingBalance"))
                      return Promise.resolve()
                    }
                  }
                ]}
              >
                <Input prefix="$" type="number" min="0.01" step="0.01" />
              </Form.Item>

              <div className={styles.tabContent}>
                {activeTab === PAYMENT_TABS.CARD && (
                  <Spin spinning={paymentMethodsLoading}>
                    {paymentMethods?.length ? (
                      <>
                        <div className={styles.cardListContainer}>
                          <PaymentMethodList
                            active={selectedCardId}
                            setActive={setSelectedCardId}
                            paymentMethods={paymentMethods}
                            clientId={clientId}
                          />
                        </div>
                        <Button
                          type="link"
                          icon={<PlusOutlined />}
                          style={{ color: colors.sportsmanBrand, fontWeight: 500, fontSize: 16, marginTop: 8 }}
                          onClick={() => setIsModalVisible(true)}
                        >
                          {t("booking.labels.add_card")}
                        </Button>
                      </>
                    ) : (
                      !isAddingCard && (
                        <Card>
                          <Empty
                            image={<NoCardImage />}
                            description={
                              <>
                                <p>{t("booking.labels.no_card_title")}</p>
                                <Button icon={<PlusOutlined />} type="primary" onClick={() => setIsModalVisible(true)}>
                                  {t("booking.labels.add_card")}
                                </Button>
                              </>
                            }
                          />
                        </Card>
                      )
                    )}
                  </Spin>
                )}

                {activeTab === PAYMENT_TABS.LINK && (
                  <Space direction="vertical" className="w-full">
                    <Flex gap={8}>
                      {isStripeConnected && (
                        <button
                          type="button"
                          className={linkProvider === LINK_PROVIDERS.STRIPE ? styles.providerButtonActive : styles.providerButton}
                          onClick={() => setLinkProvider(LINK_PROVIDERS.STRIPE)}
                        >
                          <img src={stripeIcon} alt="Stripe" height={16} />
                          {" "}Stripe
                        </button>
                      )}
                      {isPaypalConnected && isFeatureEnabled("PAYPAL_SUPPORT") && (
                        <button
                          type="button"
                          className={linkProvider === LINK_PROVIDERS.PAYPAL ? styles.providerButtonActive : styles.providerButton}
                          onClick={() => setLinkProvider(LINK_PROVIDERS.PAYPAL)}
                        >
                          <img src={paypalLogoUrl} alt="PayPal" height={16} />
                          {" "}PayPal
                        </button>
                      )}
                    </Flex>
                    <div className={styles.linkInfo}>
                      <Text color={colors.textSecondaryDark}>{linkInfoText}</Text>
                    </div>
                  </Space>
                )}

                {activeTab === PAYMENT_TABS.CASH && (
                  <div className={styles.cashInfo}>
                    <Text color={colors.textSecondaryDark}>Record a cash payment received in person.</Text>
                  </div>
                )}

                {activeTab === PAYMENT_TABS.OTHERS && (
                  <Space direction="vertical" className="w-full">
                    <Form.Item name="othersType" label="Payment Type" rules={[{ required: true, message: "Please select a payment type" }]}>
                      <Select placeholder="Select payment type" options={OTHERS_TYPES} />
                    </Form.Item>
                    <Form.Item name="note" label="Reference / Note">
                      <Input.TextArea placeholder="Add a reference number or note (optional)" rows={3} />
                    </Form.Item>
                  </Space>
                )}
              </div>

              {activeTab === PAYMENT_TABS.CARD && (
                <TipSection baseAmount={remainingBalance} onTipChange={setTipAmount} />
              )}
            </Form>
          </Space>
        </div>

        <Flex vertical gap={4} className={styles.totalRow}>
          <Flex justify="space-between">
            <Text color={colors.textSecondaryDark}>Booking Payment</Text>
            <Text color={colors.textSecondaryDark}>{numberToCurrency(baseAmount)}</Text>
          </Flex>
          {tipAmount > 0 && (
            <Flex justify="space-between">
              <Text color={colors.textSecondaryDark}>Tip</Text>
              <Text color={colors.textSecondaryDark}>+{numberToCurrency(tipAmount)}</Text>
            </Flex>
          )}
          <Flex justify="space-between">
            <Text fw={700}>Total to Pay</Text>
            <Text fw={700}>{numberToCurrency(totalToPay)}</Text>
          </Flex>
        </Flex>

        <Flex gap={spacings.S}>
          <Button className="py-l px-xl" color="primary" variant="outlined" onClick={onClose}>
            {t("global.cancel")}
          </Button>
          <Button type="primary" className="py-l px-xl" onClick={() => handleSubmit(amountValue)}>
            {submitLabel}
          </Button>
        </Flex>
      </Space>

      {isModalVisible && (
        <AddCardModal
          visible={isModalVisible}
          onCancel={() => { modalForm.resetFields(); setIsModalVisible(false) }}
          onSubmit={createPaymentMethod}
          form={modalForm}
          loading={isAddingCard}
        />
      )}
    </Spin>
  )
}

TakePaymentForm.propTypes = {
  remainingBalance: PropTypes.number,
  bookingUUID: PropTypes.string,
  bookingId: PropTypes.number,
  onClose: PropTypes.func,
  bookingTitle: PropTypes.string,
  bookedOn: PropTypes.string,
  clientName: PropTypes.string,
  clientEmail: PropTypes.string,
  experienceAvatar: PropTypes.array
}

export default TakePaymentForm
